import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { z } from 'zod';

import { OfferPage } from '~/components/offer/OfferPage';
import { useAnalytics } from '~/hooks/useAnalytics';
import { apiClient, getApiBaseUrl } from '~/lib/api';
import { readFinalScore } from '~/lib/finalScore';
import {
  fetchResolvedOffer,
  fireOfferFallbackBeacon,
  type ResolvedOfferResponse,
} from '~/lib/offerFetch';
import type { PublicLandingPage } from '~/types';
import { PublicLandingPageSchema } from '~/types/schemas';

export const Route = createFileRoute('/$slug/offer/$episodeId')({
  validateSearch: z.object({
    sessionRef: z.string().optional(),
  }),
  // R1-H1: prevent the resolved offer override HTML (which may include third-
  // party <img>/<a> elements) from leaking the offer URL via the Referer
  // header.
  head: () => ({
    meta: [
      { name: 'referrer', content: 'no-referrer' },
    ],
  }),
  component: OfferEpisodeRoute,
});

type OfferRenderMode = 'normal' | 'override_disabled';

function OfferEpisodeRoute() {
  const router = useRouter();
  const queryClient = useQueryClient();

  const { slug, landingPage } = Route.useRouteContext() as {
    slug: string;
    landingPage: PublicLandingPage;
  };
  const { episodeId } = Route.useParams();
  const { sessionRef: searchSessionRef } = Route.useSearch();
  const { trackEvent } = useAnalytics({ slug });

  // The canonical sessionRef lives in sessionStorage (written by GamePlayer at
  // finalize time). The URL search-param form is preserved only as a fallback
  // for cases where storage was cleared between navigations.
  const storedSessionRef = useMemo(() => {
    const final = readFinalScore(slug);
    return final?.sessionRef ?? searchSessionRef ?? null;
  }, [slug, searchSessionRef]);

  const [resolutionToken, setResolutionToken] = useState<string | null>(null);
  const [renderMode, setRenderMode] = useState<OfferRenderMode>('normal');

  // R5-H1: per-deployment feature gate. When false (or absent on a legacy
  // backend), skip the /offer call entirely and render defaults from the
  // by-slug payload. Non-canary orgs produce zero /offer QPS.
  const episodeOffersEnabled = landingPage.features?.episodeOffersEnabled === true;

  const {
    data: offerData,
    isLoading: offerLoading,
  } = useQuery<ResolvedOfferResponse | null>({
    queryKey: ['offer', slug, episodeId ?? null, storedSessionRef],
    enabled: episodeOffersEnabled,
    staleTime: 0,
    queryFn: async () => {
      const result = await fetchResolvedOffer(
        slug,
        episodeId ?? null,
        storedSessionRef,
      );

      if ('data' in result) {
        setResolutionToken(result.data.resolutionToken);
        if (result.data.resolvedFrom === 'override_disabled') {
          setRenderMode('override_disabled');
        }
        return result.data;
      }

      // R5-M7: bounded outage fallback hierarchy.
      // 1. Stale-if-error from the existing landingPage cache.
      // 2. If the cache is cold, single refetchQueries with the 5s timeout.
      // 3. If both fail, render the score-screen terminal state and beacon
      //    with cause='hard_fallback'.
      fireOfferFallbackBeacon(slug, result.error);

      const cached = queryClient.getQueryData<PublicLandingPage>([
        'landingPage',
        slug,
      ]);

      if (cached) {
        // Stale-if-error path — use the existing landingPage data as the
        // offer source. No extra fetch.
        return null;
      }

      // Cold cache — single refetch attempt with the same 5s budget.
      const refetchController = new AbortController();
      const refetchTimer = setTimeout(() => refetchController.abort(), 5000);
      try {
        await queryClient.fetchQuery({
          queryKey: ['landingPage', slug],
          queryFn: async () => {
            const data = await apiClient<unknown>(
              `/v1/public/landing-pages/by-slug?slug=${encodeURIComponent(slug)}`,
              { signal: refetchController.signal },
            );
            return PublicLandingPageSchema.parse(data) as PublicLandingPage;
          },
        });
        clearTimeout(refetchTimer);
        return null;
      } catch {
        clearTimeout(refetchTimer);
        // Hard fallback — score-screen terminal state (no offer, no CTA).
        setRenderMode('override_disabled');
        fireOfferFallbackBeacon(slug, 'hard_fallback');
        return null;
      }
    },
  });

  // Resolved offer fields: prefer fetched data; fall back to landing page defaults.
  const resolvedLandingPage = queryClient.getQueryData<PublicLandingPage>([
    'landingPage',
    slug,
  ]);
  const fallbackSource = resolvedLandingPage ?? landingPage;

  const offerContent =
    offerData ? offerData.offerContent : fallbackSource.offerContent;
  const forwardUrl =
    offerData ? offerData.forwardUrl : fallbackSource.forwardUrl;
  const showYakOffer =
    offerData ? offerData.showYakOffer : fallbackSource.showYakOffer;

  const handleForwardClick = useCallback(() => {
    trackEvent('offer_click');
  }, [trackEvent]);

  // R1-M2: the in-app claim-tracking button only renders when we have a
  // resolutionToken (proof the server accepted the sessionRef and bound a
  // claim record). When the token is null — no sessionRef, features flag
  // off, or override_disabled — OfferPage renders the CTA as a plain anchor.
  const useDirectClaimAnchor = resolutionToken === null;

  // In-app claim handoff URL handler. Only invoked when we have a
  // resolutionToken (token-anchored claim). For the plain-anchor path, the
  // OfferPage anchor takes over and this handler is never called.
  const handleClaimClick = useCallback(() => {
    if (resolutionToken === null) return; // defensive — anchor path active

    const final = readFinalScore(slug);
    const ref = final?.sessionRef ?? searchSessionRef;
    if (!ref) {
      // Should not happen — resolutionToken implies the server bound a
      // sessionRef — but fall back to a direct nav rather than a broken /claim.
      if (forwardUrl) {
        window.open(forwardUrl, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    const base = getApiBaseUrl();
    const url =
      `${base}/v1/public/claim/${encodeURIComponent(ref)}` +
      `?slug=${encodeURIComponent(slug)}` +
      `&token=${encodeURIComponent(resolutionToken)}`;
    window.location.href = url;
  }, [slug, forwardUrl, searchSessionRef, resolutionToken]);

  const handlePlayAgain = useCallback(() => {
    if (landingPage.targetType === 'show') {
      router.navigate({ to: '/$slug/episodes', params: { slug } });
    } else {
      router.navigate({
        to: '/$slug/game/$episodeId',
        params: { slug, episodeId: landingPage.targetId },
      });
    }
  }, [router, slug, landingPage]);

  const handleSkip = useCallback(() => {
    router.navigate({ to: '/$slug/episodes', params: { slug } });
  }, [router, slug]);

  // R1-H2 / R5-M7 hard-fallback: when overrides are disabled by the resolver
  // or the outage cascade exhausted, render the score-screen "thanks for
  // playing" terminal state — no offer page, no CTA, no Yak banner.
  if (renderMode === 'override_disabled') {
    return (
      <ScoreScreenTerminalState
        landingPage={landingPage}
        slug={slug}
      />
    );
  }

  if (offerLoading) {
    return (
      <OfferPage
        offerContent={landingPage.offerContent}
        branding={landingPage.branding}
        forwardUrl={landingPage.forwardUrl}
        title={landingPage.title}
        onClaimClick={handleClaimClick}
        onForwardClick={handleForwardClick}
        onOfferView={() => trackEvent('offer_view')}
        onSkip={handleSkip}
        targetType={landingPage.targetType}
        onPlayAgain={handlePlayAgain}
        showYakOffer={landingPage.showYakOffer}
        useDirectClaimAnchor={useDirectClaimAnchor}
      />
    );
  }

  return (
    <OfferPage
      offerContent={offerContent}
      branding={landingPage.branding}
      forwardUrl={forwardUrl}
      title={landingPage.title}
      onClaimClick={handleClaimClick}
      onForwardClick={handleForwardClick}
      onOfferView={() => trackEvent('offer_view')}
      onSkip={handleSkip}
      targetType={landingPage.targetType}
      onPlayAgain={handlePlayAgain}
      showYakOffer={showYakOffer}
      useDirectClaimAnchor={useDirectClaimAnchor}
    />
  );
}

/**
 * Terminal score-screen render used when overrides are disabled (R1-H2) or
 * the outage cascade has exhausted (R5-M7). Mirrors the SplatScoreView in
 * `$.tsx`: brand logo + score + percentage + "thanks for playing" copy. No
 * CTA, no Yak banner, no offer content.
 */
function ScoreScreenTerminalState({
  landingPage,
  slug,
}: {
  landingPage: PublicLandingPage;
  slug: string;
}) {
  const finalScore = useMemo(() => readFinalScore(slug), [slug]);
  const score = finalScore?.score ?? 0;
  const totalQuestions = finalScore?.totalQuestions ?? 0;
  const ptsPerAnswer =
    finalScore?.pointsPerCorrectAnswer ??
    landingPage.gameOptions.pointsPerCorrectAnswer;
  const maxPoints = totalQuestions * ptsPerAnswer;
  const percentage = maxPoints > 0 ? Math.round((score / maxPoints) * 100) : 0;

  return (
    <div
      className="flex h-screen w-screen items-center justify-center p-2 sm:p-4 md:p-8 lg:p-16"
      style={{
        backgroundImage:
          'url(https://s3.amazonaws.com/yak.assets/52xP82c.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      data-testid="offer-terminal-score-screen"
    >
      <div className="@container flex h-full w-full max-w-[95vw] flex-col items-center justify-center overflow-hidden rounded-xl bg-white/95 p-4 @2xl:p-8 @4xl:p-12 shadow-2xl md:aspect-video md:h-auto md:max-w-7xl md:rounded-2xl">
        <div className="flex flex-col items-center gap-4 text-center @2xl:gap-6 @4xl:gap-8">
          {landingPage.branding.logoUrl && (
            <img
              src={landingPage.branding.logoUrl}
              alt="Brand logo"
              className="h-12 @2xl:h-16 @4xl:h-20 object-contain"
            />
          )}
          <div className="flex flex-col gap-2 @2xl:gap-3 @4xl:gap-4">
            <h1
              className="text-4xl @2xl:text-5xl @4xl:text-6xl font-bold"
              style={{ color: landingPage.branding.primaryColor }}
            >
              {score}/{maxPoints}
            </h1>
            <p className="text-sm @2xl:text-base @4xl:text-lg font-medium text-gray-500">
              points earned out of {maxPoints} possible
            </p>
            <p
              className="text-2xl @2xl:text-3xl @4xl:text-4xl font-semibold"
              style={{ color: landingPage.branding.secondaryColor }}
            >
              {percentage}%
            </p>
          </div>
          <p className="text-lg @2xl:text-xl @4xl:text-2xl font-medium text-gray-700">
            Thanks for playing!
          </p>
        </div>
      </div>
    </div>
  );
}
